import { Component, computed, effect, forwardRef, input, model, output, signal, untracked } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { IPHXEditComponent } from '../../../@core/interfaces/IPHXEditComponent.interface';
import { Document, DocumentDirection, DocumentLineItem, Product, ProductType, StockAction } from '../../../generated/generated-types';
import { PhxInputNumberComponent, PhxInputNumberStatusBorderColor } from '../../../shared/components/phx-input-number/phx-input-number.component';
import { LineItemDataService } from '../../documents/services/line-item-data.service';
export type QuantityStatus = 'enough-quantity' | 'not-enough-quantity' | 'only-total-enough-quantity';
/**
 * @description Number field with border color based on if product in stock (lineItemDataService necessary to calculate if in stock)
 * @param quantityStatus - 'enough-quantity' | 'not-enough-quantity' | 'only-total-enough-quantity'
 * @param model - number
 * @param minimumNumber - number
 * @param maximumNumber - number
 */
@Component({
  selector: 'app-storage-quantity-number-input',
  templateUrl: './storage-quantity-number-input.component.html',
  styleUrls: ['./storage-quantity-number-input.component.scss'],
  standalone: true,
  imports: [
    PhxInputNumberComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StorageQuantityNumberInputComponent),
      multi: true
    }
  ]
})
export class StorageQuantityNumberInputComponent implements IPHXEditComponent, ControlValueAccessor {

  quantityStatus = input<QuantityStatus>();

  /** When false, stock availability border colors are not shown (e.g. requested quantity). */
  showStockStatus = model<boolean>(true);

  // @Input('model') model: NgModel;
  model = model<number>();

  minimumNumber = model<number>();

  maximumNumber = model<number>();

  formControl = input<FormControl>();

  quantityStep = model<number>();

  /** PHX decimalPlaces step (e.g. 0.01) — drives fraction digits on inner phx-input-number when set. */
  decimalPlaces = model<number>();

  minFractionDigits = model<number>();

  /**
   * @important if not set, it will be set to minFractionDigits,
   * if lower than minFractionDigits, it will be set to minFractionDigits - otherwise minFractionDigits would not work
   */
  maxFractionDigits = model<number>();

  /** Up/down buttons. Hidden unless a caller opts in. */
  upDownButtons = model<boolean>(false);

  productSig = model<Product>();
  storagePlaceIdSig = model<string>();

  valueChange = output<number>();

  blur = output<void>();
  focus = output<void>();

  // @Output() valueChanges = new EventEmitter<number>();

  totalStockCurrentWarehouseCached: number;
  totalStockCached: number;
  cachedDli: DocumentLineItem;
  cachedProductId: string;
  cachedStoragePlaceId: string;
  cachedQuantity: number;
  cachedDoc: Document;
  initializedSig = signal(false);
  statusBorderColorSig = signal<PhxInputNumberStatusBorderColor | null>(null);
  /** Mirrors CVA setDisabledState; bound to phx-input-number because PrimeNG 20 disabled is a read-only input signal. */
  disabledSig = signal(false);
  destroy$ = new Subject<void>();

  /**
   * Programmatic `inputNumber.min = …` no longer works on PrimeNG 20 (min is an InputSignal).
   * When minimumNumber is unset, binding undefined to [min] makes PrimeNG treat min as 0 and reject negatives.
   * -Infinity means "no lower bound"; when minimumNumber is set, align to quantityStep like before.
   */
  effectiveMin = computed(() => {
    const min = this.minimumNumber();
    const step = this.quantityStep();

    if (min === undefined || min === null) {
      return Number.NEGATIVE_INFINITY;
    }

    if (!step || step <= 0) {
      return min ?? 1;
    }

    if (min % step === 0) {
      return min;
    }

    return Math.ceil(min / step) * step;
  });

  effectiveQuantityStep = computed(() => this.quantityStep() ?? undefined);

  // ControlValueAccessor implementation
  private onChange: (value: number | null) => void = () => { };
  protected onTouched: () => void = () => { };
  private _isDisabled = false;
  private _isWritingValue = false;

  constructor(
    private lineItemDataService: LineItemDataService,
  ) {

    //doesnt work to split in 2 effects
    effect(() => {
      //needs exactly this order to work for color setting
      const currentValue = this.model();
      if (currentValue !== null && currentValue !== undefined) {
        const valueChanged = this.cachedQuantity !== currentValue;
        if (valueChanged && (this.productSig()?.id || !this.showStockStatus())) {
          this.valueChange.emit(currentValue);
        }

        // ControlValueAccessor integration - notify form control of value changes
        if (!this._isWritingValue) {
          this.onChange(currentValue);
        }
      } else {
        // Handle null/undefined values for ControlValueAccessor
        if (!this._isWritingValue) {
          this.onChange(null);
        }
      }
      if (this.cachedQuantity && this.cachedQuantity !== this.model() && this.quantityStep())
        this.setQuantityByQuantityStep(this.quantityStep());
      untracked(() => this.doStockCalcIfNeeded());

      if (this.productSig())
        this.cachedProductId = this.productSig()?.id;
      if (this.storagePlaceIdSig())
        this.cachedStoragePlaceId = this.storagePlaceIdSig();
      if (currentValue !== null && currentValue !== undefined) {
        if (this.cachedQuantity !== currentValue)
          this.cachedQuantity = currentValue;
      }
    });

    effect(() => {
      let doc = this.lineItemDataService.doc();
      //TODO can cache if document definition changes or smth like that
      untracked(() => {
        if (this.initializedSig() && doc)
          this.doStockCalc();
      })
    });

    effect(() => {
      let leftOverStockAllWarehouse = this.lineItemDataService.leftOverStockAllWarehouse();
      untracked(() => {
        if (this.initializedSig() && leftOverStockAllWarehouse !== null && leftOverStockAllWarehouse !== undefined)
          this.doStockCalc();
      })
    });

    effect(() => {
      let leftOverStockCurrentWarehouse = this.lineItemDataService.leftOverStockCurrentWarehouse();
      untracked(() => {
        if (this.initializedSig() && leftOverStockCurrentWarehouse !== null && leftOverStockCurrentWarehouse !== undefined)
          this.doStockCalc();
      })
    });

    //important - since otherwise minFractionDigits doesnt work
    effect(() => {
      const minFractionDigits = this.minFractionDigits();
      const maxFractionDigits = this.maxFractionDigits();
      untracked(() => {
        if (minFractionDigits == null || maxFractionDigits == null) {
          return;
        }
        if (maxFractionDigits < minFractionDigits) {
          this.maxFractionDigits.set(minFractionDigits);
        }
      });
    });
  }

  removeAllClasses() {
    this.statusBorderColorSig.set(null);
  }

  async doStockCalcIfNeeded() {

    if (this.cachedProductId && this.cachedQuantity) {
      if (this.cachedProductId !== this.productSig()?.id ||
        this.cachedStoragePlaceId !== this.storagePlaceIdSig() ||
        this.cachedQuantity !== this.model()
        //TODO find more props that would cause a refresh
      ) {
        this.doStockCalc();
      }
    }
    else {
      if (!this.initializedSig()) {
        this.doStockCalc();
        this.initializedSig.set(true);
      }
    }
  }

  doStockCalc() {

    if (!this.showStockStatus()) {
      this.removeAllClasses();
      return;
    }

    let doc: Document = this.lineItemDataService.doc();

    let dontNeedToCalc = doc?.documentDefinition?.documentDirection == DocumentDirection.IN && (doc?.documentDefinition?.stockAction === StockAction.RESERVE || !doc?.documentDefinition?.stockAction);

    let product = this.productSig();
    let quantity = this.model();

    if (dontNeedToCalc || !product || quantity == null || quantity == undefined || this.lineItemDataService.stockCurrentWarehouseLoadingSig() || (product.productType !== (ProductType.STORE || ProductType.SIMPLEPRODUCTION))) {
      this.removeAllClasses();
      return;
    }

    let leftOverStockAllWarehouse = this.lineItemDataService.leftOverStockAllWarehouse();
    let leftOverStockCurrentWarehouse = this.lineItemDataService.leftOverStockCurrentWarehouse();

    // TODO we have to take into account if we update a document line item. In that case we have to take the already booked quantity into account
    // eg. if we have 10 in stock and book 5, then we have 5 left. If we then update the document line item to 3, we have to check if we have 8 left in stock

    if (leftOverStockAllWarehouse == null || leftOverStockCurrentWarehouse == null) { //wait until loading everything is done
      this.removeAllClasses();
      this.refreshStyles();
      return;
    }

    this.refreshStyles();

  }

  refreshStyles() {

    if (this.productSig()) {

      this.removeAllClasses();

      let dli = this.lineItemDataService.dli();
      let docDef = this.lineItemDataService.doc()?.documentDefinition;
      if ((dli?.lineItemQuantityStatus?.quantityBackedByStock === undefined && dli?.quantity >= 0 && docDef?.documentDirection == DocumentDirection.IN) ||
        (dli?.lineItemQuantityStatus?.quantityBackedByStock >= dli?.quantity && docDef?.documentDirection == DocumentDirection.OUT) ||
        (dli?.lineItemQuantityStatus?.quantityBackedByStock <= dli?.quantity && docDef?.documentDirection == DocumentDirection.IN)) {
        return; //we go for example negative (removing quantity in delivery note)
      }

      let enoughStockCurrentWarehouse = this.lineItemDataService.leftOverStockCurrentWarehouse() >= 0;

      if (this.lineItemDataService.leftOverStockCurrentWarehouse() !== null && this.lineItemDataService.leftOverStockCurrentWarehouse() !== undefined && enoughStockCurrentWarehouse) {
        this.statusBorderColorSig.set('green');
        return;
      }

      let enoughStockAllWarehouses = this.lineItemDataService.leftOverStockAllWarehouse() >= 0;
      if (this.lineItemDataService.leftOverStockAllWarehouse() !== null && this.lineItemDataService.leftOverStockAllWarehouse() !== undefined) {
        if (enoughStockAllWarehouses === true)
          this.statusBorderColorSig.set('amber');
        else
          this.statusBorderColorSig.set('red');
      }
    }

  }

  ngOnDestroy() {
    this.model.set(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: number | null): void {
    if (value !== this.model()) {
      this._isWritingValue = true;
      this.model.set(value ?? null);
      this._isWritingValue = false;
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    // Template binding only — direct assignment on inner InputNumber.disabled is not supported on PrimeNG 20.
    this.disabledSig.set(isDisabled);
  }

  setQuantityByQuantityStep(quantityStep: number) {
    if (quantityStep && this.model() % quantityStep !== 0) {
      // round up to the nearest multiple of quantityStep
      const nearestMultiple = Math.ceil(this.model() / quantityStep) * quantityStep;
      this.model.set(nearestMultiple);
    }
  }

  onBlurEvent() {
    this.blur.emit();
    this.onTouched();
  }

  onFocusEvent() {
    this.focus.emit();
    this.onTouched();
  }
}
